import React from 'react';
import { useTranslation } from 'react-i18next';

import { Image, ShowroomSectionOptions, useGetShowroomMediaQuery, Video } from 'generated/graphql';

import { useIsShowroomMobileVersion } from 'lib/showroom';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

import ShowroomLayerMedia from 'components/Showroom/Layer/Media';

import ShowroomProductDetails from './ProductDetails';
import { useStyles } from './styles';

export type ComponentProps = {
	sectionOptions: ShowroomSectionOptions;
	file: Image;
	index: number;
};

const ShowroomProductLayer = (props: ComponentProps) => {
	const { file, index, sectionOptions } = props;

	const isMobileVersion = useIsShowroomMobileVersion();
	const classes = useStyles({ isMobileVersion });
	const { t } = useTranslation();

	const { getParams } = React.useContext(ShowroomContext);

	const { data, loading, error } = useGetShowroomMediaQuery({
		variables: {
			params: getParams,
			mediaId: file.id
		}
	});

	const showroomMedia = React.useMemo(() => (!!data ? data.showroomMedia : null), [data]);
	const selectedFields = React.useMemo(() => {
		if (!showroomMedia) return [];
		else return [...sectionOptions.productLayerSettings.samplesFields, ...sectionOptions.productLayerSettings.galleriesFields];
	}, [sectionOptions, showroomMedia]);

	return (
		<Grid
			className={classes.layerContainer}
			container
			direction={isMobileVersion ? 'column' : 'row'}
			wrap={'nowrap'}
			justifyContent="center"
			alignItems="center"
		>
			<Grid item className={classes.imageContainer}>
				<ShowroomLayerMedia file={file} index={index} imageStyle={classes.imageItemImg} position="start" />
			</Grid>
			<Grid item className={classes.detailContainer}>
				{sectionOptions.productLayerSettings?.galleriesFields?.includes('name') && (
					<h1 id={`mediaLabel`} className={classes.mediaLabel}>
						{file.name}
					</h1>
				)}
				{selectedFields.includes('globalDescription') && (
					<span id={`mediaGlobalDescription`} className={classes.globalDescription}>
						{file.globalDescription}
					</span>
				)}
				{loading ? (
					<CircularProgress className={classes.loading} />
				) : error ? (
					<Typography id="showroomMediaError" color="error">
						{t('showroom.form.productLayer.errorMessage')}
					</Typography>
				) : (
					<ShowroomProductDetails
						selectedGalleriesFields={sectionOptions.productLayerSettings.galleriesFields}
						selectedSamplesFields={sectionOptions.productLayerSettings.samplesFields}
						media={showroomMedia as Image | Video}
					/>
				)}
			</Grid>
		</Grid>
	);
};

export default React.memo(ShowroomProductLayer);
